import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface UserProfile {
  display_name: string;
  preferred_language: string;
  accessibility_settings: {
    text_size?: "normal" | "large" | "x-large";
    high_contrast?: boolean;
  };
  avatar_url: string | null;
}

interface UserRole {
  role: string;
}

interface ProfileContextType {
  profile: UserProfile | null;
  roles: UserRole[];
  primaryRole: string;
  loading: boolean;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  updateAccessibility: (settings: UserProfile["accessibility_settings"]) => Promise<void>;
  updateLanguage: (lang: string) => Promise<void>;
}

const ProfileContext = createContext<ProfileContextType>({
  profile: null,
  roles: [],
  primaryRole: "member",
  loading: true,
  updateProfile: async () => {},
  updateAccessibility: async () => {},
  updateLanguage: async () => {},
});

const ROLE_PRIORITY: Record<string, number> = {
  app_admin: 0,
  auditor: 1,
  helpdesk_agent: 2,
  accounting_manager: 3,
  officer: 4,
  group_leader: 5,
  member: 6,
};

export function ProfileProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    if (authLoading) return;
    if (!user) { setProfile(null); setRoles([]); setLoading(false); return; }
    const [profileRes, rolesRes] = await Promise.all([
      supabase.from("profiles").select("display_name, preferred_language, accessibility_settings, avatar_url").eq("user_id", user.id).maybeSingle(),
      supabase.from("user_roles").select("role").eq("user_id", user.id),
    ]);
    if (profileRes.data) {
      setProfile({
        ...profileRes.data,
        accessibility_settings: (profileRes.data.accessibility_settings as any) ?? {},
      });
    }
    setRoles((rolesRes.data as any) ?? []);
    setLoading(false);
  }, [user, authLoading]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const primaryRole = roles.length > 0
    ? roles.sort((a, b) => (ROLE_PRIORITY[a.role] ?? 99) - (ROLE_PRIORITY[b.role] ?? 99))[0].role
    : "member";

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) return;
    await supabase.from("profiles").update(updates as any).eq("user_id", user.id);
    await fetchProfile();
  };

  const updateAccessibility = async (settings: UserProfile["accessibility_settings"]) => {
    if (!user || !profile) return;
    const merged = { ...profile.accessibility_settings, ...settings };
    await supabase.from("profiles").update({ accessibility_settings: merged as any }).eq("user_id", user.id);
    await fetchProfile();
  };

  const updateLanguage = async (lang: string) => {
    if (!user) return;
    await supabase.from("profiles").update({ preferred_language: lang }).eq("user_id", user.id);
    await fetchProfile();
  };

  return (
    <ProfileContext.Provider value={{ profile, roles, primaryRole, loading, updateProfile, updateAccessibility, updateLanguage }}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  return useContext(ProfileContext);
}
